import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Receipt, Paperclip, Download, Loader2 } from 'lucide-react';
import { vendorApi } from '../../services/api';
import { applyTenantBranding } from '../../utils/portal';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import StatusBadge from '../../components/StatusBadge';
import { formatDate, formatDeduction, isBlankDate } from '../../utils/format';
import { downloadBlob, exportCsv, fileNameFrom } from '../../utils/exportCsv';

type View = 'open' | 'deducted';

const views: { id: View; label: string; description: string }[] = [
  { id: 'open', label: 'Kommande', description: 'Utgifter och avdrag som dras vid nästa avräkning' },
  { id: 'deducted', label: 'Dragna', description: 'Utgifter och avdrag som redan dragits på en avräkning' },
];

/**
 * Who an expense comes from (BC expenseSource): an external vendor's invoice, or this company
 * invoicing the vendor. Older BC versions don't send it; then the customer fields are all there is.
 */
function expenseFrom(e: any): { name: string; reference: string } {
  if (e.expenseSource === 'externalVendor') {
    return {
      name: e.expenseFromName || 'Extern leverantör',
      reference: e.externalDocumentNo ? `Faktura ${e.externalDocumentNo}` : '',
    };
  }
  if (e.expenseSource === 'invoicedToVendor') {
    return {
      name: e.expenseFromName ? `${e.expenseFromName} (fakturerat er)` : 'Fakturerat er',
      reference: e.expenseOriginDocNo ? `Faktura ${e.expenseOriginDocNo}` : '',
    };
  }
  return { name: e.customerNo || '', reference: e.customerReference || '' };
}

/** The files attached to one expense (receipts), loaded when the row is opened */
function Receipts({ documentId }: { documentId: string }) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['vendor', 'attachments', documentId],
    queryFn: async () => (await vendorApi.getDocumentAttachments(documentId)).data,
  });

  const download = async (attachment: any) => {
    setBusyId(attachment.id);
    setFailed(false);
    try {
      const response = await vendorApi.getDocumentAttachment(documentId, attachment.id);
      const fallback = `${attachment.fileName}${attachment.fileExtension ? '.' + attachment.fileExtension : ''}`;
      downloadBlob(response.data, fileNameFrom(response.headers['content-disposition'], fallback));
    } catch {
      setFailed(true);
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Hämtar bilagor…</p>;
  if (isError) return <p role="alert" className="text-sm text-red-700">Bilagorna kunde inte hämtas.</p>;
  const attachments: any[] = data?.attachments || [];
  if (attachments.length === 0) return <p className="text-sm text-gray-500">Inga bilagor.</p>;

  return (
    <div>
      <ul className="flex flex-wrap gap-2">
        {attachments.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => download(a)}
              disabled={busyId !== null}
              className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm text-ink ring-1 ring-inset ring-gray-200 transition-all duration-300 ease-fluid hover:ring-blue-300 active:scale-[0.98] disabled:opacity-60"
            >
              {busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
              {a.fileName}
              {a.fileExtension ? `.${String(a.fileExtension).toLowerCase()}` : ''}
            </button>
          </li>
        ))}
      </ul>
      {failed && <p role="alert" className="mt-2 text-sm text-red-700">Filen kunde inte hämtas. Försök igen.</p>}
    </div>
  );
}

export default function OpenExpenses() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = searchParams.get('view') === 'deducted' ? 'deducted' : 'open';
  const [openRow, setOpenRow] = useState<string | null>(null);

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => (await vendorApi.me()).data,
  });

  const { data: expensesData, isLoading, isError } = useQuery({
    queryKey: ['vendor', 'settlement-expenses', view],
    queryFn: async () => (await vendorApi.getSettlementExpenses(view)).data,
  });

  useEffect(() => {
    if (error) navigate('/login');
  }, [error, navigate]);

  useEffect(() => {
    if (vendorData?.tenant?.primaryColor) applyTenantBranding(vendorData.tenant.primaryColor);
  }, [vendorData]);

  const expenses: any[] = expensesData?.expenses || [];
  const totalAmount = expensesData?.totalAmount ?? expenses.reduce((sum, e) => sum + (e.deductionAmount ?? e.expenseAmount ?? 0), 0);
  const current = views.find((v) => v.id === view)!;

  const exportRows = () =>
    exportCsv(`utgifter-${view}-${new Date().toISOString().slice(0, 10)}`, [
      { header: 'Datum', value: (e: any) => e.orderDate || e.documentDate },
      { header: 'Dokumentnr', value: (e: any) => e.no },
      { header: 'Order-ID', value: (e: any) => e.orderId },
      { header: 'Resurs', value: (e: any) => e.resourceNo },
      { header: 'Från', value: (e: any) => expenseFrom(e).name },
      { header: 'Referens', value: (e: any) => expenseFrom(e).reference },
      { header: 'Kommentar', value: (e: any) => e.comment },
      { header: 'Avräkning', value: (e: any) => (e.status === 'Order' ? '' : e.internalDocumentNo) },
      { header: 'Bilagor', value: (e: any) => e.attachmentCount ?? '' },
      { header: 'Belopp', value: (e: any) => e.deductionAmount ?? e.expenseAmount ?? 0 },
    ], expenses);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar logoUrl={vendorData?.tenant?.logoUrl} companyName={vendorData?.tenant?.companyName} />

      <div className="flex flex-1 flex-col">
        <TopBar userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username} />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Utgifter och avdrag</h1>
              <p className="mt-1 text-gray-600">{current.description}</p>
            </div>
            <button
              type="button"
              onClick={exportRows}
              disabled={expenses.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink ring-1 ring-inset ring-gray-200 transition-all duration-300 ease-fluid hover:ring-blue-300 active:scale-[0.98] disabled:opacity-50"
            >
              <Download className="h-4 w-4" aria-hidden />
              Exportera till Excel
            </button>
          </div>

          <div role="tablist" aria-label="Status" className="mb-6 inline-flex gap-1 rounded-full bg-white p-1 ring-1 ring-inset ring-gray-200">
            {views.map((v) => (
              <button
                key={v.id}
                role="tab"
                type="button"
                aria-selected={view === v.id}
                onClick={() => {
                  setOpenRow(null);
                  setSearchParams(v.id === 'open' ? {} : { view: v.id });
                }}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-300 ease-fluid ${
                  view === v.id ? 'bg-navy text-white' : 'text-gray-600 hover:text-ink'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          <div className="card mb-6 grid grid-cols-2 gap-6 p-6">
            <div>
              <p className="text-sm text-gray-600">Antal</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{expenses.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{view === 'open' ? 'Dras vid nästa avräkning' : 'Totalt draget'}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{formatDeduction(totalAmount)}</p>
            </div>
          </div>

          <div className="card">
            {isLoading ? (
              <div className="p-12 text-center text-gray-500">Laddar utgifter…</div>
            ) : isError ? (
              <p role="alert" className="p-6 text-sm text-red-800">Utgifterna kunde inte hämtas. Försök igen om en stund.</p>
            ) : expenses.length === 0 ? (
              <div className="p-12 text-center">
                <Receipt className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden />
                <h2 className="mb-2 text-base font-semibold text-ink">{view === 'open' ? 'Inga kommande avdrag' : 'Inga dragna utgifter'}</h2>
                <p className="text-gray-600">
                  {view === 'open' ? 'Inget dras vid nästa avräkning.' : 'Det finns inga utgifter på bokförda avräkningar ännu.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Datum', 'Dokument', 'Från', view === 'open' ? 'Status' : 'Avräkning', 'Bilaga'].map((h) => (
                        <th key={h} scope="col" className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                      ))}
                      <th scope="col" className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Belopp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {expenses.map((expense) => {
                      const rowId = expense.systemId;
                      const open = openRow === rowId;
                      const hasAttachments = expense.attachmentCount > 0;
                      return (
                        <Fragment key={rowId}>
                          <tr>
                            <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-900">
                              {formatDate(isBlankDate(expense.orderDate) ? expense.documentDate : expense.orderDate)}
                            </td>
                            <td className="px-5 py-3.5 text-sm">
                              <span className="font-mono font-medium text-ink">{expense.no}</span>
                              {expense.comment && <span className="block max-w-[40ch] text-xs text-gray-500">{expense.comment}</span>}
                            </td>
                            <td className="px-5 py-3.5 text-sm">
                              <span className="text-gray-900">{expenseFrom(expense).name || '–'}</span>
                              {expenseFrom(expense).reference && <span className="block text-xs text-gray-500">{expenseFrom(expense).reference}</span>}
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                              {view === 'open' ? (
                                <StatusBadge tone="turquoise">Nästa avräkning</StatusBadge>
                              ) : expense.internalDocumentNo ? (
                                <Link
                                  to={`/invoices/${expense.settlementDocumentType || 'invoice'}/${encodeURIComponent(expense.internalDocumentNo)}`}
                                  className="font-mono text-blue-700 hover:underline"
                                >
                                  {expense.internalDocumentNo}
                                </Link>
                              ) : (
                                '–'
                              )}
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                              {hasAttachments ? (
                                <button
                                  type="button"
                                  aria-expanded={open}
                                  onClick={() => setOpenRow(open ? null : rowId)}
                                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-blue-700 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-50"
                                >
                                  <Paperclip className="h-3.5 w-3.5" aria-hidden />
                                  {expense.attachmentCount} {expense.attachmentCount === 1 ? 'bilaga' : 'bilagor'}
                                </button>
                              ) : (
                                <span className="text-gray-400">–</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-medium tabular-nums text-gray-900">
                              {formatDeduction(expense.deductionAmount ?? expense.expenseAmount)}
                            </td>
                          </tr>
                          {open && (
                            <tr className="bg-gray-50/70">
                              <td colSpan={6} className="px-5 py-4">
                                <Receipts documentId={rowId} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
